/**
 * Planning together with accounts (paywall plan P2), redesign 2026-11 (docs/DESIGN.md, canvas artboard 12):
 *
 *   <CollabButton>    the menubar pill: "Plan together", then the avatars of who's here + "Live · 3" (+ the upload pill)
 *   <InviteButton>    green "Invite" pill for owners and admins while live
 *   <CollabPanel>     the Plan together sheet, inside the map card: live status, invite link, people, world files
 *   <CollabActivity>  "Robin joined · just now" at the top of the map (only real events: someone new in the live room)
 *   <AccountInviteSection> / <SharedWorldsList>   the #invite=… card and "Shared worlds" in the setup wizard
 *
 * Whether the sheet is open lives in the store (`collabOpen`), so anything can open it. Styles: share.css.
 */
import { Check, ChevronDown, Copy as CopyIcon, Eye, Link2, LoaderCircle, Pencil, Plus, TriangleAlert, UserMinus, Users, X, CirclePause, Info } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { controller } from "../app/controller.ts";
import { DEFAULT_SERVER, useApp, type RoomState } from "../state/store.ts";
import { openSignIn, useAccount } from "../lib/auth.ts";
import { daysLeft, loadPlans, openLiveWorlds, openPlans, useBilling, type BillingStatus } from "../lib/billing.ts";
import { CloudUpload, UploadPill } from "./CloudUpload.tsx";
import { AcademyLink } from "./Academy.tsx";
import {
  createInvite, fmtBytes, listInvites, members as loadMembers, myWorlds, removeMember, revokeInvite, saveLink, savedLinks, setRole,
  type InviteRow, type MembersInfo, type MyWorld, type Role,
} from "../lib/collab.ts";
import "./share.css";

/** what a role may do, in the words of the pickers (the role names stay in ROLE_LABEL for the rest of the app) */
const CAN: Record<Role, string> = { owner: "Owner", admin: "Admin", editor: "Can edit", viewer: "Can view" };

const openSheet = () => useApp.getState().set({ collabOpen: true });

// ---------------- small parts ----------------

export function initialOf(name: string | null | undefined) {
  const ch = [...(name ?? "").trim()][0];
  return ch ? ch.toUpperCase() : "?";
}

/** light text on dark identity colours (old random cursor colours), dark text on the rest */
function darkColour(hex: string) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex ?? "");
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const lin = (c: number) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const l = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return l < 0.18;
}

function Avatar({ name, color, className = "", here }: { name: string; color: string; className?: string; here?: boolean }) {
  return (
    <span className={`avatar pt-av${darkColour(color) ? " ink-light" : ""}${className ? " " + className : ""}`} style={{ "--c": color } as React.CSSProperties} aria-hidden="true">
      {initialOf(name)}
      {here !== undefined && <i className={`pt-presence${here ? " on" : ""}`} />}
    </span>
  );
}

function CopyButton({ text, label = "Copy", className = "btn btn-sm btn-outline" }: { text: string; label?: string; className?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className={className}
      onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 2000); } catch { /* select it by hand */ } }}
    >
      {done ? <><Check size={14} aria-hidden="true" />Copied</> : <><CopyIcon size={14} aria-hidden="true" />{label}</>}
    </button>
  );
}

/** a quiet picker that looks like "Can edit ⌄" until it's clicked (a real <select> for keyboard and screen readers) */
function RolePicker({ value, roles, onChange, label, disabled }: { value: Role; roles: Role[]; onChange: (r: Role) => void; label: string; disabled?: boolean }) {
  return (
    <span className="pt-role">
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as Role)} aria-label={label}>
        {roles.map((r) => <option key={r} value={r}>{CAN[r]}</option>)}
      </select>
      <ChevronDown size={13} aria-hidden="true" />
    </span>
  );
}

function Note({ tone = "info", icon, children, actions }: { tone?: "info" | "warn" | "danger"; icon?: typeof Info; children: React.ReactNode; actions?: React.ReactNode }) {
  const Icon = icon ?? (tone === "info" ? Info : TriangleAlert);
  return (
    <div className={`pt-note ${tone}`} role={tone === "danger" ? "alert" : undefined}>
      <Icon size={15} aria-hidden="true" />
      <div className="pt-note-body">
        <div>{children}</div>
        {actions && <div className="pt-note-actions">{actions}</div>}
      </div>
    </div>
  );
}

const Sep = () => <span className="pt-sep" aria-hidden="true">·</span>;

/** "Cloud trial, 7 days left" for the owner; "Shared by Joni" for everyone else */
function planFact(room: RoomState, billing: BillingStatus | null): string | null {
  if (room.kind !== "account") return room.ownerName ? `Shared by ${room.ownerName}` : null;
  if (room.role !== "owner") return room.ownerName ? `Shared by ${room.ownerName}` : null;
  const p = billing?.plan;
  if (p && p.plan !== "free") {
    if (p.source === "trial") {
      const d = daysLeft(p.expires_at);
      return `${/trial$/i.test(p.name) ? p.name : `${p.name} trial`}, ${d <= 0 ? "ends today" : d === 1 ? "1 day left" : `${d} days left`}`;
    }
    return /plan$/i.test(p.name) ? p.name : `${p.name} plan`;
  }
  return room.plan ? `${room.plan.name} plan` : null;
}

function sharedWhen(ms: number) {
  const d = new Date(ms);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const day = new Date(); day.setHours(0, 0, 0, 0);
  const diff = Math.floor((day.getTime() - new Date(d).setHours(0, 0, 0, 0)) / 86400_000);
  if (diff <= 0) return `Shared today ${time}`;
  if (diff === 1) return `Shared yesterday ${time}`;
  return `Shared ${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })} ${time}`;
}

const shortDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

// ---------------- the menubar pill ----------------

/** "Plan together" → the avatars of who's here + "Live · 3". Keeps `.collab-btn` (onboarding + e2e click it). */
export function CollabButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const status = useApp((s) => s.collab.status);
  const peers = useApp((s) => s.collab.peers);
  const myName = useApp((s) => s.collab.name);
  const profile = useAccount((s) => s.profile);
  const live = status === "connected" || status === "idle";
  const me = { name: profile?.displayName || myName || "You", color: profile?.color || "#6fbf4a" };
  const people = status === "connected" ? [me, ...peers] : [me];
  const n = people.length;
  const shown = people.slice(0, 3);
  const title = status === "connected" ? (n === 1 ? "Only you here" : `${n} people here: ${people.map((p) => p.name).join(", ")}`)
    : status === "idle" ? "Paused while you're away · reconnects when you're back"
    : status === "error" ? "Can't reach the server" : "Plan this world with friends";

  return (
    <>
      {!open && <UploadPill onOpen={openSheet} />}
      <button
        className={`btn btn-pill collab-btn ${status}${live ? " is-live" : ""}`}
        aria-pressed={open}
        title={title}
        onClick={onToggle}
        data-tour="collab"
      >
        {live ? (
          <>
            <span className="avatar-stack" aria-hidden="true">
              {shown.map((p, i) => <Avatar key={i} name={p.name} color={p.color} />)}
              {n > shown.length && <span className="avatar pt-more">+{n - shown.length}</span>}
            </span>
            <span className="pt-pill-state"><i className="pt-live-dot" />{status === "idle" ? "Live · paused" : `Live · ${n}`}</span>
          </>
        ) : status === "connecting" ? (
          <><LoaderCircle className="spin" size={16} aria-hidden="true" />Connecting…</>
        ) : (
          <><Users className="pt-users" size={16} aria-hidden="true" />Plan together{status === "error" && <i className="pt-err-dot" aria-hidden="true" />}</>
        )}
      </button>
    </>
  );
}

/** green "Invite" next to the pill: owners and admins, while live */
export function InviteButton() {
  const show = useApp((s) => s.collab.status === "connected" && s.room?.kind === "account" && !s.room.paused && (s.room.role === "owner" || s.room.role === "admin"));
  if (!show) return null;
  return <button className="btn btn-pill btn-primary invite-btn" onClick={openSheet} title="Create and copy an invite link">Invite</button>;
}

// ---------------- the Plan together sheet ----------------

export function CollabPanel({ onClose }: { onClose: () => void }) {
  const collab = useApp((s) => s.collab);
  const room = useApp((s) => s.room);
  const guestOf = useApp((s) => s.guestOf);
  const share = useApp((s) => s.share);
  const demo = useApp((s) => s.demo);
  const signedIn = useAccount((s) => s.status === "signed-in");
  const profileName = useAccount((s) => s.profile?.displayName);
  const billing = useBilling((s) => s.status);
  // worlds of accounts: your name is your account's name (one name everywhere: cursor, People list, the others' screens)
  const accountName = signedIn && room?.kind !== "legacy" && !!profileName;
  const [name, setName] = useState((accountName ? profileName : collab.name) || profileName || "");
  useEffect(() => { if (accountName && profileName) setName(profileName); }, [accountName, profileName]);
  const [url, setUrl] = useState(collab.url);
  const [asking, setAsking] = useState<"stop" | "leave" | null>(null);

  const status = collab.status;
  const connected = status === "connected";
  const offline = status === "offline" || status === "error" || status === "ended";
  const live = !offline; // connected, connecting or paused while away
  const account = room?.kind === "account" ? room : null;
  const manage = !!account && !account.paused && (account.role === "owner" || account.role === "admin");
  const server = guestOf ?? collab.url;
  const owner = account?.role === "owner";
  // a guest whose shared world is gone (removed, or the owner stopped sharing): only their own copy is left
  const orphan = !!guestOf && !room;
  const canGoLive = (!!room || !!guestOf || signedIn) && !orphan && !demo;
  const tooMany = owner && !!billing?.plan && billing.plan.max_worlds > 0 && (billing.usage.worlds ?? 0) > billing.plan.max_worlds;
  const goLive = () => controller.connect(guestOf ?? (url.trim() || collab.url || DEFAULT_SERVER), name);

  useEffect(() => { if (!room) setAsking(null); }, [room]);

  const stateWord = connected ? "Live" : status === "idle" ? "Live · paused" : status === "connecting" ? "Connecting…" : status === "error" ? "Can't reach the server" : status === "ended" ? "Session ended" : "Offline";
  const fact = room ? planFact(room, billing) : null;
  const here = collab.peers.length + 1;

  return (
    <section
      className="popover collab-panel pt-sheet glass-2"
      aria-label="Plan together"
      onKeyDown={(e) => { if (e.key === "Escape" && !(e.target as HTMLElement).closest("select")) { e.stopPropagation(); onClose(); } }}
    >
      <header className="pt-head">
        <div className="pt-title-row">
          <h2 className="pt-title">Plan together</h2>
          <span className="pt-head-actions">
            {live && <button className="btn btn-sm btn-ghost pt-leave" onClick={() => controller.disconnect()} title="Leave the live session · the others stay">Leave</button>}
            <button className="btn btn-sm btn-icon pt-close" onClick={onClose} aria-label="Close" title="Close · Esc"><X size={15} aria-hidden="true" /></button>
          </span>
        </div>
        {(room || live || status === "error") && (
          <div className="pt-status" aria-live="polite">
            <span className={`pt-state ${status}`}><i className={`pt-dot ${status}`} />{stateWord}</span>
            {connected && <><Sep /><span className="tabular">{here === 1 ? "Only you here" : `${here} people here`}</span></>}
            {fact && <><Sep /><span>{fact}</span></>}
          </div>
        )}
      </header>

      <div className="pt-body">
        {/* ----- what's going on (paused, view only, idle, ended, errors) ----- */}
        {(account?.paused || account?.role === "viewer" || room?.kind === "legacy" || status === "idle" || (status === "ended" && room) || (share.state === "error" && !connected) || orphan || demo) && (
          <div className="pt-sec pt-notes">
            {account?.paused && (owner ? (
              <Note tone="warn" actions={<>
                <button className="btn btn-sm btn-primary primary" onClick={() => openPlans("Pick a plan to plan together in this world again.")}>See plans</button>
                {tooMany && <button className="btn btn-sm btn-outline" onClick={openLiveWorlds}>Choose which stay live</button>}
              </>}>
                This world is paused: {tooMany ? `your ${billing!.plan.name} plan keeps ${billing!.plan.max_worlds} world${billing!.plan.max_worlds === 1 ? "" : "s"} live` : "your plan doesn't cover it any more"}. Everyone can still open it and export, but not edit together. Nothing is deleted.
              </Note>
            ) : (
              <Note tone="warn">This world is paused: {account.ownerName ?? "the owner"}'s plan doesn't cover it any more. You can still look around and export.</Note>
            ))}
            {account?.role === "viewer" && !account.paused && (
              <Note icon={Eye}>You can look around, not change it. Ask {account.ownerName ?? "the owner"} to make you an editor.</Note>
            )}
            {room?.kind === "legacy" && <Note>You joined through an old link, shared before accounts. Your edits sync live.</Note>}
            {status === "idle" && (
              <Note icon={CirclePause}>Paused while you're away (hidden tab or no input for a while). It reconnects as soon as you're back; your changes are kept and sync then.</Note>
            )}
            {status === "ended" && room && <Note tone="warn">The live session ended. Your plans are still saved here. Rejoin once the host is live again.</Note>}
            {share.state === "error" && !connected && <Note tone="danger">{share.msg}</Note>}
            {orphan && <Note>You're not in this shared world any more. Your copy of the plan stays in this browser.</Note>}
            {demo && <Note>The demo island stays on this computer. Open your own world to plan together. <AcademyLink to="shareAWorld" about="sharing a world" /></Note>}
          </div>
        )}

        {/* ----- signed out ----- */}
        {!room && !guestOf && !signedIn && !demo && (
          <div className="pt-sec pt-intro">
            <span className="pt-intro-icon" aria-hidden="true"><Users size={18} /></span>
            <p className="pt-lead">Plan this world with friends, live. Everyone sees the same plan and each other's cursors.</p>
            <p className="pt-text">Planning together needs a free account. Planning alone never does. <AcademyLink to="shareAWorld" about="sharing a world" /></p>
            <button className="btn btn-lg btn-block btn-primary primary" onClick={() => openSignIn("Planning together needs a free account. Planning alone never does.")}>Sign in or create an account</button>
          </div>
        )}

        {/* ----- not live: your name + Go live / Rejoin ----- */}
        {offline && canGoLive && (
          <div className="pt-sec pt-golive">
            {!room && !guestOf && (
              <p className="pt-text">Going live makes a private shared copy of this world. Friends join with an invite link; your block textures stay on your computer. <AcademyLink to="shareAWorld" about="sharing a world" /></p>
            )}
            <label className="pt-field">
              <span className="field-label">Your name</span>
              <input className="input" value={name} maxLength={40} placeholder="Joni" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") goLive(); }} />
              {accountName && <span className="field-hint">Your account's name, shown to everyone on your cursor and in People.</span>}
            </label>
            <button className="btn btn-lg btn-block btn-primary primary" onClick={goLive}>{status === "ended" && room ? "Rejoin" : "Go live"}</button>
          </div>
        )}

        {/* ----- account worlds: invite link · people · world files ----- */}
        {account && <RoomSections server={server} roomId={account.id} manage={manage} ownerView={owner} filesOpen={manage && (connected || share.state === "uploading")} />}

        {/* ----- worlds shared before accounts ----- */}
        {room?.kind === "legacy" && connected && controller.inviteUrl() && (
          <div className="pt-sec">
            <div className="pt-sec-head"><span className="overline">Invite link</span></div>
            <div className="pt-link">
              <Link2 size={14} aria-hidden="true" />
              <input readOnly value={controller.inviteUrl()} onFocus={(e) => e.target.select()} aria-label="Invite link" />
              <CopyButton text={controller.inviteUrl()} label="Copy" className="btn btn-primary" />
            </div>
          </div>
        )}
        {connected && !account && <LivePeople />}

        {!guestOf && !room && !demo && (
          <details className="pt-sec pt-advanced">
            <summary>Advanced: server</summary>
            <label className="pt-field">
              <span className="field-label">Server URL</span>
              <input className="input mono" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={DEFAULT_SERVER} />
            </label>
            {url !== DEFAULT_SERVER && <button className="btn btn-sm btn-ghost pt-tight" onClick={() => setUrl(DEFAULT_SERVER)}>Use the default <span className="mono">{DEFAULT_SERVER}</span></button>}
          </details>
        )}
      </div>

      {/* ----- the footer: session and sharing (two clicks for what can't be undone) ----- */}
      {asking ? (
        <footer className="pt-foot pt-asking">
          <p className="pt-text">
            {asking === "stop"
              ? "Delete the shared copy? Its files, members and invite links go from the server. Everyone keeps the plan in their own browser."
              : `Leave “${room?.name ?? "this world"}” for good? Your copy of the plan stays in this browser; you need a new invite link to come back.`}
          </p>
          <div className="pt-foot-row">
            <button className="btn btn-sm btn-ghost" onClick={() => setAsking(null)}>Cancel</button>
            <button className="btn btn-sm danger" onClick={() => { const a = asking; setAsking(null); void (a === "stop" ? controller.stopSharing() : controller.leaveSharedWorld()); }}>
              {asking === "stop" ? "Delete the shared copy" : "Leave for good"}
            </button>
          </div>
        </footer>
      ) : ((connected && controller.isHost()) || account) && (
        <footer className="pt-foot">
          {connected && controller.isHost()
            ? <button className="btn pt-end" onClick={() => controller.endSessionForEveryone()} title="Disconnect everyone and close the room until someone goes live again">End session for everyone</button>
            : <span />}
          {account && (owner
            ? <button className="btn pt-destructive" onClick={() => setAsking("stop")}>Stop sharing…</button>
            : <button className="btn pt-destructive" onClick={() => setAsking("leave")}>Leave this world…</button>)}
        </footer>
      )}
    </section>
  );
}

/** worlds shared before accounts (and anything without a members list): who's live right now */
function LivePeople() {
  const peers = useApp((s) => s.collab.peers);
  return (
    <div className="pt-sec">
      <div className="pt-sec-head"><span className="overline">People</span><span className="pt-count">{peers.length + 1} here</span></div>
      <ul className="pt-people member-list">
        <MyRow role={null} here />
        {peers.map((p, i) => (
          <li key={i} className="pt-person" data-here="true">
            <Avatar name={p.name} color={p.color} here />
            <span className="pt-person-main"><span className="pt-person-name">{p.name}</span><span className="pt-person-sub">Here now</span></span>
          </li>
        ))}
      </ul>
      {!peers.length && <p className="pt-text">Nobody else is here yet.</p>}
    </div>
  );
}

/** your own row: your name (rename in place: it's your account's name, so it changes everywhere) */
function MyRow({ role, here, side }: { role: Role | null; here: boolean; side?: React.ReactNode }) {
  const collabName = useApp((s) => s.collab.name);
  const profile = useAccount((s) => s.profile);
  const room = useApp((s) => s.room);
  const account = room?.kind === "account" && !!profile;
  const name = (account ? profile!.displayName : collabName) || profile?.displayName || collabName || "You";
  const color = profile?.color || "#6fbf4a";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const commit = () => { setEditing(false); if (draft.trim() && draft.trim() !== name) controller.setMyName(draft); };
  return (
    <li className="pt-person me" data-here={here ? "true" : undefined}>
      <Avatar name={name} color={color} here={here} />
      <span className="pt-person-main">
        {editing ? (
          <input
            className="pt-name-input" value={draft} maxLength={40} autoFocus aria-label="Your name, shown to everyone"
            onChange={(e) => setDraft(e.target.value)} onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { e.stopPropagation(); setDraft(name); setEditing(false); } }}
          />
        ) : (
          <span className="pt-person-name">
            <span className="pt-name">{name}</span> <span className="pt-you">(you)</span>
            <button className="btn btn-icon btn-sm pt-rename" onClick={() => { setDraft(name); setEditing(true); }} aria-label="Change your name" title="Change your name · shown to everyone"><Pencil size={12} aria-hidden="true" /></button>
          </span>
        )}
        <span className="pt-person-sub">{here ? "Here now" : "Not here"}</span>
      </span>
      {side ?? (role && <span className="pt-person-role">{CAN[role]}</span>)}
    </li>
  );
}

/** Invite links, members and the world's files of an account world (live: the server says when people join or leave). */
function RoomSections({ server, roomId, manage, ownerView, filesOpen }: { server: string; roomId: string; manage: boolean; ownerView: boolean; filesOpen: boolean }) {
  const me = useAccount((s) => s.user?.id);
  const roomRev = useApp((s) => s.roomRev);
  const connected = useApp((s) => s.collab.status === "connected");
  // who's live and under which name: when that changes (someone came, went, or renamed), the list is asked again
  const presence = useApp((s) => s.collab.peers.map((p) => `${p.uid ?? ""}\t${p.name}`).sort().join("\n"));
  const myName = useAccount((s) => s.profile?.displayName);
  const uploading = useApp((s) => s.share.state === "uploading");
  const filesShared = useApp((s) => s.share.state === "shared");
  const sharedAt = useApp((s) => (s.share.state === "shared" ? s.share.manifest?.updatedAt ?? 0 : 0));
  const teamMax = useBilling((s) => s.plans.find((p) => p.id === "team")?.max_members ?? null);
  const [info, setInfo] = useState<MembersInfo | null>(null);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [links, setLinks] = useState<Record<number, string>>(() => savedLinks(roomId));
  const [role, setNewRole] = useState<Role>("editor");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setInfo(await loadMembers(server, roomId));
      if (manage) setInvites(await listInvites(server, roomId));
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [server, roomId, manage]);
  useEffect(() => { void reload(); }, [reload, roomRev, sharedAt, presence, myName]);
  // "Team fits 50 people": the limits come from the plans table
  useEffect(() => { if (ownerView && !useBilling.getState().plans.length) void loadPlans(server).catch(() => {}); }, [ownerView, server]);
  // while the world's files go up, the storage meter follows along
  useEffect(() => {
    if (!uploading) return;
    const t = setInterval(() => void reload(), 4000);
    return () => clearInterval(t);
  }, [uploading, reload]);

  // uid → the name they go by right now (a rename shows at once, before the list is asked again)
  const liveName = new Map(presence ? presence.split("\n").map((l) => l.split("\t") as [string, string]).filter(([u]) => u) : []);
  const online = new Set(liveName.keys());
  if (connected && me) online.add(me);
  const nameOf = (m: { user_id: string; display_name: string }) => (m.user_id === me ? myName : liveName.get(m.user_id)) || m.display_name;

  const act = async (f: () => Promise<unknown>) => {
    setBusy(true);
    try { await f(); await reload(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const newLink = () => act(async () => {
    const r = await createInvite(server, roomId, { role, days: 7, maxUses: 10 });
    saveLink(roomId, r.invite.id, r.link);
    setLinks(savedLinks(roomId));
    setInvites((l) => [r.invite, ...l.filter((i) => i.id !== r.invite.id)]);
  });

  const max = info?.plan.max_members ?? 0;
  const full = !!info && info.members >= max;
  const roles: Role[] = info?.plan.allow_admins && ownerView ? ["admin", "editor", "viewer"] : ["editor", "viewer"];
  // the link shown: the newest one for the picked role that this browser made (the server keeps only a hash)
  const ofRole = invites.filter((i) => i.role === role);
  const current = ofRole.find((i) => links[i.id]) ?? null;
  const others = invites.filter((i) => i !== current);
  const seatsLeft = Math.max(0, max - (info?.members ?? 0));
  const onTeam = info?.plan.plan === "team" || info?.plan.plan === "beta";
  // one green per screen: until the world files are shared, "Share world" below is the one
  const green = filesShared || !filesOpen;

  return (
    <>
      {manage && (
        <div className="pt-sec pt-invite">
          <div className="pt-sec-head">
            <span className="overline">Invite link</span>
            <RolePicker value={role} roles={roles} onChange={setNewRole} label="Role for people who use the link" />
          </div>
          <div className="pt-link">
            <Link2 size={14} aria-hidden="true" />
            {current
              ? <input readOnly value={links[current.id]} onFocus={(e) => e.target.select()} aria-label="Invite link" />
              : <span className="pt-link-empty">{ofRole.length ? "Made in another browser: make a new one to copy it here" : "No link yet"}</span>}
            {current
              ? <CopyButton text={links[current.id]} className={green ? "btn btn-primary primary" : "btn btn-outline"} />
              : <button className={green ? "btn btn-primary primary" : "btn btn-outline"} disabled={busy || full} onClick={() => void newLink()}><Plus size={14} aria-hidden="true" />Create link</button>}
          </div>
          {full
            ? <Note tone="warn">This world is full: {max} people on the {info?.plan.name} plan. Remove someone{!onTeam ? " or upgrade to Team" : ""} to invite more.</Note>
            : <p className="pt-text">Works for 7 days, up to 10 people. Friends sign in free; their blocks come from their own game or Mojang's official download. <AcademyLink to="inviteRoles" about="invites and roles" /></p>}
          {current && (
            <div className="pt-link-actions">
              <button className="btn btn-sm btn-ghost" disabled={busy || full} onClick={() => void newLink()}>New link</button>
              <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => void act(() => revokeInvite(server, roomId, current.id))}>Revoke</button>
              <span className="pt-link-meta tabular">{current.uses} of {current.max_uses} used · until {shortDate(current.expires_at)}</span>
            </div>
          )}
          {others.length > 0 && (
            <details className="pt-more-links">
              <summary>{others.length === 1 ? "1 more link works" : `${others.length} more links work`}</summary>
              <ul className="invite-list">
                {others.map((i) => (
                  <li key={i.id}>
                    <span className="pt-invite-meta"><b>{CAN[i.role]}</b> · {i.uses} of {i.max_uses} used · until {shortDate(i.expires_at)}</span>
                    <span className="pt-invite-acts">
                      {links[i.id] && <CopyButton text={links[i.id]} className="btn btn-sm btn-ghost" />}
                      <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => void act(() => revokeInvite(server, roomId, i.id))}>Revoke</button>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="pt-sec pt-people-sec">
        <div className="pt-sec-head">
          <span className="overline">People</span>
          {info && <span className="pt-count tabular" title={`${info.plan.name} plan: up to ${max} people in a world`}>{info.members} of {max}</span>}
        </div>
        {err && <p className="pt-text pt-error">{err}</p>}
        <ul className="pt-people member-list">
          {info?.list.map((m) => {
            const here = online.has(m.user_id);
            const canChange = manage && m.role !== "owner" && m.user_id !== me && !(info.role === "admin" && m.role === "admin");
            const side = canChange ? (
              removing === m.user_id ? (
                <span className="pt-person-side">
                  <button className="btn btn-sm danger" disabled={busy} onClick={() => { setRemoving(null); void act(() => removeMember(server, roomId, m.user_id)); }}>Remove</button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setRemoving(null)}>Cancel</button>
                </span>
              ) : (
                <span className="pt-person-side">
                  <RolePicker value={m.role} roles={roles.includes(m.role) ? roles : [m.role, ...roles]} disabled={busy} label={`Role of ${nameOf(m)}`}
                    onChange={(r) => void act(() => setRole(server, roomId, m.user_id, r))} />
                  <button className="btn btn-icon btn-sm pt-remove" onClick={() => setRemoving(m.user_id)} aria-label={`Remove ${nameOf(m)}`} title={`Remove ${nameOf(m)} from this world`}><UserMinus size={14} aria-hidden="true" /></button>
                </span>
              )
            ) : <span className="pt-person-role">{CAN[m.role]}</span>;
            if (m.user_id === me) return <MyRow key={m.user_id} role={m.role} here={here} side={side} />;
            return (
              <li key={m.user_id} className={`pt-person${here ? "" : " away"}`} data-here={here ? "true" : undefined}>
                <Avatar name={nameOf(m)} color={m.color} here={here} />
                <span className="pt-person-main">
                  <span className="pt-person-name"><span className="pt-name">{nameOf(m)}</span></span>
                  <span className="pt-person-sub">{here ? "Here now" : `Not here · joined ${shortDate(m.joined_at)}`}</span>
                </span>
                {side}
              </li>
            );
          })}
        </ul>
        {info && manage && (
          <div className="pt-seats">
            <span className="pt-seat" aria-hidden="true"><Plus size={14} /></span>
            <span>
              {seatsLeft === 0 ? "No seats left." : seatsLeft === 1 ? "1 seat left." : `${seatsLeft} seats left.`}
              {ownerView && !onTeam && teamMax && teamMax > max ? ` Team fits ${teamMax} people.` : ""}
            </span>
          </div>
        )}
      </div>

      {filesOpen && <WorldFiles info={info} />}
    </>
  );
}

/** the world's files on the server: share / update them, how much of the owner's storage they use */
function WorldFiles({ info }: { info: MembersInfo | null }) {
  const share = useApp((s) => s.share);
  const guestOf = useApp((s) => s.guestOf);
  const used = info?.owner_storage_bytes ?? 0;
  const cap = info?.plan.storage_bytes ?? 0;
  const frac = cap > 0 ? used / cap : 0;
  const pct = typeof share.frac === "number" && share.step === "upload" ? ` ${Math.round(share.frac * 100)}%` : "…";
  return (
    <div className="pt-sec pt-files">
      <div className="pt-sec-head">
        <span className="overline">World files</span>
        {share.state === "shared" && <span className="pt-files-state ok"><Check size={13} aria-hidden="true" />Up to date</span>}
        {share.state === "uploading" && <span className="pt-files-state busy tabular"><LoaderCircle className="spin" size={13} aria-hidden="true" />Uploading{pct}</span>}
        {share.state === "idle" && <span className="pt-files-state">Not shared yet</span>}
        {share.state === "error" && <span className="pt-files-state bad"><TriangleAlert size={13} aria-hidden="true" />Upload failed</span>}
      </div>
      {share.state === "uploading" && <CloudUpload />}
      {share.state === "error" && <Note tone="danger">{share.msg}</Note>}
      {info && (
        <>
          <div className={`meter${frac >= 1 ? " full" : frac >= 0.85 ? " warn" : ""}`} role="meter" aria-label="Cloud storage" aria-valuemin={0} aria-valuemax={cap} aria-valuenow={used}>
            <i style={{ width: `${Math.min(100, Math.max(used > 0 ? 1 : 0, frac * 100))}%` }} />
          </div>
          <div className="pt-meta">
            <span className="tabular" title="Cloud storage of all worlds you share">{fmtBytes(used)} of {fmtBytes(cap)}</span>
            {share.state === "shared" && share.manifest && <span className="tabular" title={`${share.manifest.files.length} files`}>{sharedWhen(share.manifest.updatedAt)}</span>}
          </div>
        </>
      )}
      <p className="pt-text faint">World files and map colours, so friends can join. Block textures never leave anyone's computer. <AcademyLink to="shareAWorld" about="sharing a world" /></p>
      {guestOf
        ? <p className="pt-text">Open the world file on your computer to update the shared files.</p>
        : share.state !== "uploading" && (
          <div className="pt-files-actions">
            {share.state === "shared"
              ? <button className="btn btn-sm btn-outline" onClick={() => void controller.shareCurrentWorld()}>Update shared world</button>
              : <button className="btn btn-primary primary" onClick={() => void controller.shareCurrentWorld()}>Share world</button>}
          </div>
        )}
    </div>
  );
}

// ---------------- "Robin joined" at the top of the map ----------------

/**
 * Someone new came into the live room: a short note at the top of the map. Only real events: people who were already
 * here when you connected, or who come back after a pause, aren't announced again. Mount it inside the map card.
 */
export function CollabActivity() {
  const status = useApp((s) => s.collab.status);
  const peers = useApp((s) => s.collab.peers);
  const sheet = useApp((s) => s.collabOpen);
  const seen = useRef<Set<string> | null>(null);
  const since = useRef(0);
  const [event, setEvent] = useState<{ names: string[]; color: string; key: number } | null>(null);

  useEffect(() => {
    if (status === "offline" || status === "ended") { seen.current = null; return; }
    if (status !== "connected") return;
    if (!seen.current) { seen.current = new Set(); since.current = Date.now(); }
    const key = (p: { uid?: string; name: string }) => p.uid || `name:${p.name}`;
    const fresh = peers.filter((p) => !seen.current!.has(key(p)));
    if (!fresh.length) return;
    for (const p of fresh) seen.current.add(key(p));
    // whoever is in the room in the first moments was here before you
    if (Date.now() - since.current < 4000) return;
    setEvent({ names: fresh.map((p) => p.name), color: fresh[fresh.length - 1].color, key: Date.now() });
  }, [status, peers]);
  useEffect(() => {
    if (!event) return;
    const t = setTimeout(() => setEvent(null), 6000);
    return () => clearTimeout(t);
  }, [event]);
  if (!event || status !== "connected") return null;
  const who = event.names.length === 1 ? event.names[0] : event.names.length === 2 ? `${event.names[0]} and ${event.names[1]}` : `${event.names[0]} and ${event.names.length - 1} others`;
  return (
    <div key={event.key} className={`pt-activity${sheet ? " beside-sheet" : ""}`} role="status">
      <Avatar name={event.names[event.names.length - 1]} color={event.color} />
      <span><b>{who}</b> joined</span>
      <span className="pt-when">just now</span>
    </div>
  );
}

// ---------------- the setup screen ----------------

/** An #invite=… link: whose world, which role, sign in (free), join. */
export function AccountInviteSection() {
  const ai = useApp((s) => s.accountInvite)!;
  const status = useAccount((s) => s.status);
  const p = ai.preview;
  const ok = p?.status === "ok";
  const dead: Record<string, string> = {
    invalid: "This invite link doesn't work. Ask for a new one.",
    expired: "This invite link has expired. Ask for a new one.",
    revoked: "This invite link was switched off. Ask for a new one.",
    used_up: "This invite link has been used up. Ask for a new one.",
  };
  const as = p?.role === "viewer" ? "Can view: you can look around, not change it" : p?.role === "admin" ? "Admin: can invite and manage people" : "Can edit";
  return (
    <section className="join pt-join">
      <h2>Join a shared world</h2>
      {!p && !ai.error && <p className="pt-join-wait"><LoaderCircle className="spin" size={16} aria-hidden="true" />Opening the invite…</p>}
      {ai.error && <Note tone="danger">{ai.error}</Note>}
      {p && !ok && <Note tone="warn">{dead[p.status] ?? dead.invalid}</Note>}
      {ok && (
        <>
          <div className="join-card pt-join-card">
            <span className="pt-join-icon" aria-hidden="true"><Users size={20} /></span>
            <span className="pt-join-text">
              <b className="pt-join-title">{p.owner_name ?? "Someone"} invited you to plan “{p.world_name}”</b>
              <span className="pt-join-meta">
                <span>{as}</span><Sep /><span className="tabular">{p.members} of {p.max_members} people</span><Sep /><span>joining is free</span>
              </span>
            </span>
          </div>
          {p.full && <Note tone="warn">This world is full. Ask {p.owner_name ?? "the owner"} to make room or upgrade.</Note>}
          {p.paused && !p.full && <Note>This world is paused right now: you can join, look around and export, but not edit together until {p.owner_name ?? "the owner"} picks a plan.</Note>}
          {status === "signed-in"
            ? <button className="btn btn-lg btn-block btn-primary primary" disabled={ai.busy || p.full} onClick={() => void controller.acceptAccountInvite()}>{ai.busy ? <><LoaderCircle className="spin" size={16} aria-hidden="true" />Joining…</> : "Join"}</button>
            : <button className="btn btn-lg btn-block btn-primary primary" disabled={status === "checking"} onClick={() => openSignIn("Joining is free: you only need an account.")}>Sign in to join (free)</button>}
          <p className="pt-text">Planning alone never needs an account. It's only for planning together, so people know who's who.</p>
        </>
      )}
      <button className="btn btn-sm btn-ghost pt-own" onClick={() => { useApp.getState().set({ accountInvite: null }); history.replaceState(null, "", location.pathname); }}>Open my own world instead</button>
    </section>
  );
}

/** Signed in: the shared worlds you own or joined (open one straight from the server). */
export function SharedWorldsList({ disabled }: { disabled: boolean }) {
  const status = useAccount((s) => s.status);
  const [worlds, setWorlds] = useState<MyWorld[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const server = DEFAULT_SERVER;
  useEffect(() => {
    if (status !== "signed-in") { setWorlds(null); return; }
    let alive = true;
    myWorlds(server).then((w) => { if (alive) setWorlds(w); }, (e: Error) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [status, server]);
  if (status !== "signed-in" || (!worlds?.length && !err)) return null;
  return (
    <div className="shared-worlds pt-worlds">
      <div className="overline">Shared worlds</div>
      {err && <p className="pt-text pt-error">{err}</p>}
      {worlds?.map((w) => (
        <button key={w.room_id} className="pt-world" disabled={disabled} onClick={() => void controller.prepareSharedWorld(server, w.room_id)}
          title={w.role === "owner" ? "Opens the shared copy from the server. To update the files, open the world from your computer." : undefined}>
          <span className="pt-world-icon" aria-hidden="true"><Users size={18} /></span>
          <span className="pt-world-text">
            <b>{w.name}</b>
            <span className="pt-world-meta">{w.role === "owner" ? "Yours" : `By ${w.owner_name ?? "someone"}`}<Sep />{w.role === "owner" ? "Owner" : CAN[w.role]}{w.paused ? <><Sep />Paused</> : null}</span>
          </span>
          <span className="pt-world-when">{activeWhen(w.last_active_at)}</span>
        </button>
      ))}
    </div>
  );
}

function activeWhen(iso: string) {
  const t = Date.parse(iso);
  if (!t) return "";
  const days = Math.floor((Date.now() - t) / 86400_000);
  if (days <= 0) return "Active today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return shortDate(iso);
}
